import classnames from 'clsx';
import { uniqueId } from 'lodash';
import type { FC, HTMLAttributes } from 'react';
import React, { Children, useCallback, useEffect, useState } from 'react';

import Option from './Option';
import OptionContext from './OptionContext';
import styles from './index.scss';

interface ToggleStatic {
  Option: typeof Option;
}

interface ToggleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  onChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  value?: string;
}

const Toggle: FC<ToggleProps> & ToggleStatic = ({
  id: idProp,
  className,
  children,
  onChange,
  disabled,
  name: nameProp,
  value: valueProp,
  ...other
}) => {
  const id = idProp || uniqueId('widget-toggle');
  const name = nameProp || `name-${id}`;
  const [value, setValue] = useState(valueProp);

  const handleChange = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      setValue(target.value);
      if (onChange) {
        onChange(target.value);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (onChange) {
      setValue(valueProp);
    }
  }, [onChange, valueProp]);

  const count = Children.count(children);

  return (
    <div
      id={id}
      className={classnames(
        {
          [styles.toggle]: true,
          [styles.toggleOf2]: count === 2
        },
        className
      )}
      {...other}
    >
      <OptionContext.Provider
        value={{
          disabled,
          name,
          selectedValue: value,
          onChange: handleChange
        }}
      >
        {children}
        <div className={styles.toggleSlider} />
      </OptionContext.Provider>
    </div>
  );
};

Toggle.displayName = 'WidgetToggle';
Toggle.Option = Option;
export default Toggle;
